import React, { useCallback, useEffect, useState } from 'react';
import { createIntakeResponse, type IntakeField, type IntakeForm, type IntakeResponse } from '../api/events';

interface Props {
  eventId: number;
  form: IntakeForm | null;
  fields: IntakeField[];
  isVoid: boolean;
  /** Shown when there are no fields; only true for editors. */
  canEdit?: boolean;
  /** Called after a response is saved so callers can refresh their lists. */
  onSubmitted?: (response: IntakeResponse) => void;
}

const THANK_YOU = 'Thank you for your response!';

/** Shared "Fill out" form for an event intake form. Resets and thanks on submit. */
const EventIntakeFill: React.FC<Props> = ({ eventId, form, fields, isVoid, canEdit, onSubmitted }) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const emptyAnswers = useCallback(
    () => Object.fromEntries(fields.map((f) => [String(f.id), ''])) as Record<string, string>,
    [fields]
  );

  useEffect(() => {
    setAnswers(emptyAnswers());
  }, [emptyAnswers]);

  const setAnswer = (fieldId: number, v: string) => {
    setAnswers((prev) => ({ ...prev, [String(fieldId)]: v }));
    if (success) setSuccess('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVoid) return;
    for (const f of fields) {
      if (f.required && !(answers[String(f.id)] ?? '').trim()) {
        setError(`"${f.label}" is required.`);
        setSuccess('');
        return;
      }
    }
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const created = await createIntakeResponse(eventId, answers);
      onSubmitted?.(created);
      setAnswers(emptyAnswers());
      setSuccess(THANK_YOU);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save submission.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (field: IntakeField) => {
    const id = `intake-fill-${field.id}`;
    const value = answers[String(field.id)] ?? '';
    const common = { id, className: 'form-input', value, disabled: submitting || isVoid };
    if (field.field_type === 'textarea') {
      return <textarea {...common} rows={3} onChange={(e) => setAnswer(field.id, e.target.value)} />;
    }
    if (field.field_type === 'select') {
      return (
        <select {...common} onChange={(e) => setAnswer(field.id, e.target.value)}>
          <option value="">Select…</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    const typeMap: Record<string, string> = { text: 'text', email: 'email', number: 'number', phone: 'tel', date: 'date' };
    return <input {...common} type={typeMap[field.field_type] ?? 'text'} onChange={(e) => setAnswer(field.id, e.target.value)} />;
  };

  return (
    <div className="intake-fill">
      <h3 className="intake-section-title">{form?.title || 'Event intake form'}</h3>
      {form?.description ? <p className="page-subtitle intake-fill-desc">{form.description}</p> : null}
      {fields.length === 0 ? (
        <p className="page-subtitle">
          This form has no fields yet.{canEdit ? ' Use the Builder tab to add fields.' : ''}
        </p>
      ) : (
        <form className="intake-fill-form" onSubmit={(e) => void submit(e)}>
          {fields.map((field) => (
            <div key={field.id} className="form-group">
              <label htmlFor={`intake-fill-${field.id}`} className="form-label">
                {field.label}
                {field.required ? <span className="intake-required-star"> *</span> : null}
              </label>
              {renderInput(field)}
            </div>
          ))}
          {error ? (
            <p className="create-order-error" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="intake-fill-success" role="status">
              {success}
            </p>
          ) : null}
          <button type="submit" className="update-button" disabled={submitting || isVoid}>
            {submitting ? 'Saving…' : 'Submit'}
          </button>
          {isVoid ? <p className="page-subtitle">This event is closed; the form is read-only.</p> : null}
        </form>
      )}
    </div>
  );
};

export default EventIntakeFill;
